import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  BackgroundProcessor, 
  supportsBackgroundProcessors
} from '@livekit/track-processors';
import { useLocalParticipant } from '@livekit/components-react';
import { Track, ParticipantEvent } from 'livekit-client';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import './BackgroundEffects.css';

export function BackgroundEffects() {
  const { localParticipant } = useLocalParticipant();
  const [processor, setProcessor] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [currentMode, setCurrentMode] = useState('disabled');
  const [blurRadius, setBlurRadius] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const objectUrlRef = useRef(null);

  // Track when camera is available so we re-run init when user turns video on
  const [cameraTrackAvailable, setCameraTrackAvailable] = useState(false);

  // Check browser support
  useEffect(() => {
    const supported = supportsBackgroundProcessors();
    setIsSupported(supported);
    
    if (!supported) {
      console.warn('Background effects are not supported in this browser');
    }
  }, []);

  // Subscribe to camera track publish/unpublish so we re-init when user starts/stops video
  useEffect(() => {
    if (!localParticipant) return;

    const checkCamera = () => {
      const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
      const hasTrack = !!(cameraPub?.track);
      setCameraTrackAvailable(hasTrack);
    };

    checkCamera();
    localParticipant.on(ParticipantEvent.LocalTrackPublished, checkCamera);
    localParticipant.on(ParticipantEvent.LocalTrackUnpublished, checkCamera);

    return () => {
      localParticipant.off(ParticipantEvent.LocalTrackPublished, checkCamera);
      localParticipant.off(ParticipantEvent.LocalTrackUnpublished, checkCamera);
    };
  }, [localParticipant]);

  // When camera turns off, clear processor so we don't hold a stale reference
  useEffect(() => {
    if (!cameraTrackAvailable && processor) {
      const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
      if (cameraPub?.track?.getProcessor()) {
        try {
          cameraPub.track.stopProcessor();
        } catch (e) {
          // track may already be stopped
        }
      }
      setProcessor(null);
      setCurrentMode('disabled');
    }
  }, [cameraTrackAvailable, localParticipant, processor]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  // Lazy init: only attach processor when user opens the menu (avoids room disconnect on first video start)
  const ensureProcessor = useCallback(async () => {
    if (!isSupported || !localParticipant || !cameraTrackAvailable) return null;
    const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
    if (!cameraPub?.track) return null;
    const videoTrack = cameraPub.track;
    if (videoTrack.getProcessor()) {
      setProcessor(videoTrack.getProcessor());
      return videoTrack.getProcessor();
    }
    try {
      const newProcessor = BackgroundProcessor({ mode: 'disabled' });
      await videoTrack.setProcessor(newProcessor);
      setProcessor(newProcessor);
      const saved = localStorage.getItem('backgroundEffect');
      if (saved) {
        try {
          const { mode, blurRadius: savedRadius } = JSON.parse(saved);
          if (mode === 'blur' && savedRadius) {
            await newProcessor.switchTo({ mode: 'background-blur', blurRadius: savedRadius });
            setCurrentMode('blur');
            setBlurRadius(savedRadius);
          }
        } catch (e) {
          console.error('Failed to restore saved effect:', e);
        }
      }
      return newProcessor;
    } catch (error) {
      console.error('Failed to initialize processor:', error);
      toast.error('Background effects initialization failed.');
      return null;
    }
  }, [isSupported, localParticipant, cameraTrackAvailable]);

  // Apply blur effect
  const applyBlur = useCallback(async (radius) => {
    if (!processor) {
      toast.error('Please wait, initializing...');
      return;
    }

    setIsLoading(true);
    try {
      await processor.switchTo({ 
        mode: 'background-blur', 
        blurRadius: radius 
      });
      setCurrentMode('blur');
      setBlurRadius(radius);
      
      // Save preference
      localStorage.setItem('backgroundEffect', JSON.stringify({
        mode: 'blur',
        blurRadius: radius
      }));
      
      toast.success(`Background blur applied`);
    } catch (error) {
      console.error('Failed to apply blur:', error);
      toast.error('Failed to apply blur effect');
    } finally {
      setIsLoading(false);
    }
  }, [processor]);

  // Apply virtual background
  const applyVirtualBackground = useCallback(async (imagePath) => {
    if (!processor) {
      toast.error('Please wait, initializing...');
      return;
    }

    setIsLoading(true);
    try {
      await processor.switchTo({ 
        mode: 'virtual-background', 
        imagePath: imagePath 
      });
      setCurrentMode('virtual');
      
      // Save preference
      localStorage.setItem('backgroundEffect', JSON.stringify({
        mode: 'virtual',
        imagePath
      }));
      
      toast.success('Virtual background applied');
    } catch (error) {
      console.error('Failed to apply virtual background:', error);
      toast.error('Failed to apply virtual background');
    } finally {
      setIsLoading(false);
    }
  }, [processor]);

  // Disable effects
  const disableEffects = useCallback(async () => {
    if (!processor) return;

    setIsLoading(true);
    try {
      await processor.switchTo({ mode: 'disabled' });
      setCurrentMode('disabled');
      
      // Clear preference
      localStorage.removeItem('backgroundEffect');
      
      toast.success('Background effects disabled');
    } catch (error) {
      console.error('Failed to disable effects:', error);
      toast.error('Failed to disable effects');
    } finally {
      setIsLoading(false);
    }
  }, [processor]);

  // Handle file upload for custom background
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum size: 5MB');
      return;
    }

    // Create object URL and apply
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const imageUrl = URL.createObjectURL(file);
    objectUrlRef.current = imageUrl;
    await applyVirtualBackground(imageUrl);
  };

  const handleToggleMenu = useCallback(async () => {
    if (showSettings) {
      setShowSettings(false);
      return;
    }
    if (!cameraTrackAvailable) {
      toast.info('Turn on your camera to use background effects.');
      return;
    }
    if (!processor) {
      setIsLoading(true);
      setShowSettings(true);
      // Defer processor init so we don't run heavy init during publish/reconnect
      await new Promise((r) => setTimeout(r, 2000));
      await ensureProcessor();
      setIsLoading(false);
      return;
    }
    setShowSettings(true);
  }, [showSettings, cameraTrackAvailable, processor, ensureProcessor]);

  if (!isSupported) {
    return null; // Don't show controls if not supported
  }

  return (
    <div className="background-effects-control">
      <button 
        className="lk-button"
        onClick={handleToggleMenu}
        title={!cameraTrackAvailable ? "Turn on camera for background effects" : "Background Effects"}
        disabled={!cameraTrackAvailable}
        style={{ opacity: !cameraTrackAvailable ? 0.5 : 1 }}
      >
        <Icon icon={currentMode === 'blur' ? 'mdi:blur' : currentMode === 'virtual' ? 'mdi:image' : 'mdi:video'} width="24" />
      </button>

      {showSettings && (
        <div className="background-effects-menu">
          <div className="menu-header">
            <h4>Background Effects</h4>
            <button 
              className="close-btn"
              onClick={() => setShowSettings(false)}
            >
              <Icon icon="mdi:close" width="20" />
            </button>
          </div>
          
          {/* Disable Button */}
          <button
            className={`effect-btn ${currentMode === 'disabled' ? 'active' : ''}`}
            onClick={disableEffects}
            disabled={isLoading || currentMode === 'disabled'}
          >
            <Icon icon="mdi:video" width="20" />
            <span>No Effect</span>
          </button>

          {/* Blur Options */}
          <div className="effect-section">
            <button
              className={`effect-btn ${currentMode === 'blur' ? 'active' : ''}`}
              onClick={() => applyBlur(blurRadius)}
              disabled={isLoading}
            >
              <Icon icon="mdi:blur" width="20" />
              <span>Background Blur</span>
            </button>
            
            {currentMode === 'blur' && (
              <div className="blur-slider">
                <label>Blur Strength: {blurRadius}</label>
                <input
                  type="range"
                  min="5"
                  max="20"
                  value={blurRadius}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setBlurRadius(value);
                  }}
                  onMouseUp={() => applyBlur(blurRadius)}
                  onTouchEnd={() => applyBlur(blurRadius)}
                />
              </div>
            )}
          </div>

          {/* Virtual Background Options */}
          <div className="effect-section">
            <p className="section-title">Virtual Backgrounds</p>
            <div className="bg-thumbnails">
              <button
                onClick={() => applyVirtualBackground('https://images.unsplash.com/photo-1497366216548-37526070297c?w=800')}
                disabled={isLoading}
                className="bg-thumb"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=400)' }}
                title="Office"
              />
              <button
                onClick={() => applyVirtualBackground('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800')}
                disabled={isLoading}
                className="bg-thumb"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400)' }}
                title="Nature"
              />
              <button
                onClick={() => applyVirtualBackground('https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=800')}
                disabled={isLoading}
                className="bg-thumb"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=400)' }}
                title="Gradient"
              />
            </div>
            
            {/* Upload custom background */}
            <label className="upload-btn">
              <Icon icon="mdi:upload" width="16" />
              <span>Upload Image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
              <span>Applying effect...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
